// /ops/provider — external-credit provider findings (SM-665, SM-664,
// SM-621 phase 15): the provider_funding findings grouped by provider
// broker, currency and day, the case queue (state + owner), and a trace
// lookup by provider transaction, funding intent or order.
//
// A leaf route (no children), so its search params cannot leak anywhere.
// Read-only apart from the links into each finding; nothing on this page
// edits a balance.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { CaseStateBadge, ProviderTracePanel } from "@/components/ops/providerOps";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { adminName, useAdminMap } from "@/hooks/useAdminNames";
import { useMyPerms } from "@/hooks/useMyPerms";
import { fmtRelative } from "@/lib/format";
import { fetchFindings, opsKeys } from "@/lib/ops-queries";
import {
  SETTABLE_CASE_STATES,
  severityLabel,
  type CaseState,
  type Finding,
  type ProviderTraceQuery,
} from "@/lib/ops-types";

type TraceKind = "transaction_id" | "intent_id" | "order_id";

interface Search {
  case_state?: CaseState;
  /** "none" = unassigned, "me" = the caller, absent = anyone. */
  owner?: "none" | "me";
  broker_id?: string;
  only_open?: string;
  trace_kind?: TraceKind;
  trace_id?: string;
}

const TRACE_KINDS: { value: TraceKind; label: string }[] = [
  { value: "transaction_id", label: "Provider transaction" },
  { value: "intent_id", label: "Funding intent" },
  { value: "order_id", label: "Order" },
];

const idParam = (v: unknown): string | undefined =>
  typeof v === "string" && v.trim() !== "" && v.length <= 128 ? v.trim() : undefined;

export const Route = createFileRoute("/_app/ops/provider")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    case_state: SETTABLE_CASE_STATES.find((o) => o.value === s.case_state)?.value,
    owner: s.owner === "none" || s.owner === "me" ? s.owner : undefined,
    broker_id: idParam(s.broker_id),
    only_open: s.only_open === "false" ? "false" : undefined,
    trace_kind: TRACE_KINDS.find((k) => k.value === s.trace_kind)?.value,
    trace_id: idParam(s.trace_id),
  }),
  component: ProviderOpsPage,
});

const PAGE = 200;

/** The provider broker a finding belongs to: the column, else the details. */
export function findingBroker(f: Finding): string {
  const d = f.details ?? {};
  for (const k of ["broker_id", "root_broker_id"]) {
    const v = d[k];
    if (typeof v === "string" && v !== "") return v;
  }
  return "";
}

function dayOf(ms: number): string {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export interface ProviderGroup {
  broker: string;
  currency: string;
  day: string;
  total: number;
  critical: number;
  byCode: Record<string, number>;
}

/** Findings grouped by provider broker × currency × day first seen, newest day first. */
export function groupProviderFindings(items: Finding[]): ProviderGroup[] {
  const m = new Map<string, ProviderGroup>();
  for (const f of items) {
    const broker = findingBroker(f);
    const cur = typeof f.details?.currency === "string" ? (f.details.currency as string) : "";
    const day = dayOf(f.first_detected_at_unix_ms);
    const key = `${broker}|${cur}|${day}`;
    const g = m.get(key) ?? { broker, currency: cur, day, total: 0, critical: 0, byCode: {} };
    g.total++;
    if (f.severity === 1) g.critical++;
    g.byCode[f.finding_code] = (g.byCode[f.finding_code] ?? 0) + 1;
    m.set(key, g);
  }
  return [...m.values()].sort((a, b) =>
    a.day !== b.day ? b.day.localeCompare(a.day) : a.broker.localeCompare(b.broker),
  );
}

function ProviderOpsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  const admins = useAdminMap();
  const onlyOpen = search.only_open !== "false";
  const owner = search.owner === "me" ? perms.adminId || undefined : search.owner;

  const params = {
    task_type: ["provider_funding" as const],
    only_open: onlyOpen,
    case_state: search.case_state ? [search.case_state] : undefined,
    owner,
    broker_id: search.broker_id ? [search.broker_id] : undefined,
    limit: PAGE,
  };
  const q = useQuery({
    queryKey: opsKeys.findings(params),
    queryFn: () => fetchFindings(params),
    refetchInterval: 30_000,
    // "Mine" waits for the caller's admin id rather than listing everyone's.
    enabled: search.owner !== "me" || !!perms.adminId,
  });
  const items = useMemo(() => q.data?.Items ?? [], [q.data]);
  const groups = useMemo(() => groupProviderFindings(items), [items]);

  const [traceKind, setTraceKind] = useState<TraceKind>(search.trace_kind ?? "transaction_id");
  const [traceId, setTraceId] = useState(search.trace_id ?? "");
  const traceQuery: ProviderTraceQuery | null =
    search.trace_kind && search.trace_id
      ? ({ [search.trace_kind]: search.trace_id } as ProviderTraceQuery)
      : null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Provider funding findings</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <Select
              label="Case state"
              placeholder="Any state"
              options={SETTABLE_CASE_STATES}
              value={search.case_state ?? ""}
              onChange={(e) =>
                void navigate({ search: (s) => ({ ...s, case_state: (e.target.value as CaseState) || undefined }) })
              }
            />
            <Select
              label="Owner"
              placeholder="Anyone"
              options={[
                { value: "me", label: "Assigned to me" },
                { value: "none", label: "Unassigned" },
              ]}
              value={search.owner ?? ""}
              onChange={(e) =>
                void navigate({
                  search: (s) => ({ ...s, owner: (e.target.value as "me" | "none") || undefined }),
                })
              }
            />
            <Input
              label="Provider broker"
              placeholder="Broker id"
              defaultValue={search.broker_id ?? ""}
              onBlur={(e) => void navigate({ search: (s) => ({ ...s, broker_id: idParam(e.target.value) }) })}
            />
            <label className="flex items-end gap-2 pb-2 text-sm">
              <input
                type="checkbox"
                checked={onlyOpen}
                onChange={(e) =>
                  void navigate({ search: (s) => ({ ...s, only_open: e.target.checked ? undefined : "false" }) })
                }
              />
              Open findings only
            </label>
          </div>

          {q.isLoading ? (
            <Skeleton className="h-40" />
          ) : q.isError ? (
            <p className="text-sm text-red-500">{(q.error as Error).message}</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted">No provider funding findings match these filters.</p>
          ) : (
            <>
              {q.data?.NextCursor ? (
                <p className="text-xs text-yellow-500">
                  Showing the newest findings only. Narrow the filters to see the rest.
                </p>
              ) : null}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted">
                      <th className="pb-1 pr-3 font-normal">Day first seen</th>
                      <th className="pb-1 pr-3 font-normal">Provider broker</th>
                      <th className="pb-1 pr-3 font-normal">Currency</th>
                      <th className="pb-1 pr-3 font-normal">Findings</th>
                      <th className="pb-1 pr-3 font-normal">Critical</th>
                      <th className="pb-1 pr-3 font-normal">By code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <tr key={`${g.broker}|${g.currency}|${g.day}`} className="border-t border-border align-top">
                        <td className="py-1 pr-3 font-mono text-xs">{g.day}</td>
                        <td className="py-1 pr-3">
                          {g.broker ? (
                            <button
                              type="button"
                              className="text-neon hover:underline"
                              onClick={() => void navigate({ search: (s) => ({ ...s, broker_id: g.broker }) })}
                            >
                              {g.broker}
                            </button>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="py-1 pr-3">{g.currency || "—"}</td>
                        <td className="py-1 pr-3">{g.total}</td>
                        <td className="py-1 pr-3">{g.critical > 0 ? <Badge tone="danger">{g.critical}</Badge> : "0"}</td>
                        <td className="py-1 pr-3">
                          <ul className="space-y-0.5">
                            {Object.entries(g.byCode).map(([code, n]) => (
                              <li key={code} className="font-mono text-2xs">
                                <span>{code}</span> <span className="text-muted">×{n}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="divide-y divide-border rounded border border-border">
                {items.map((f) => (
                  <li key={f.finding_id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                    <Badge tone={f.severity === 1 ? "danger" : f.severity === 2 ? "warning" : "muted"}>
                      {severityLabel(f.severity)}
                    </Badge>
                    <Link
                      to="/ops/findings/$id"
                      params={{ id: f.finding_id }}
                      className="font-mono text-xs text-neon hover:underline"
                    >
                      {f.finding_code}
                    </Link>
                    <span className="font-mono text-2xs text-muted">{f.entity_id}</span>
                    <CaseStateBadge state={f.case_state} />
                    <span className="text-xs text-muted">
                      {f.owner_admin_id ? adminName(admins, f.owner_admin_id) : "Unassigned"}
                    </span>
                    <span className="ml-auto text-xs text-muted">
                      {fmtRelative(new Date(f.last_detected_at_unix_ms).toISOString())}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Trace a provider record</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <form
            className="grid gap-3 md:grid-cols-[14rem_1fr_auto] md:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              const id = idParam(traceId);
              void navigate({ search: (s) => ({ ...s, trace_kind: id ? traceKind : undefined, trace_id: id }) });
            }}
          >
            <Select
              label="Look up by"
              options={TRACE_KINDS}
              value={traceKind}
              onChange={(e) => setTraceKind(e.target.value as TraceKind)}
            />
            <Input
              label="Identifier"
              placeholder="Transaction, intent or order id"
              value={traceId}
              onChange={(e) => setTraceId(e.target.value)}
            />
            <Button type="submit" disabled={!idParam(traceId)}>
              Trace
            </Button>
          </form>
          {traceQuery ? (
            <ProviderTracePanel query={traceQuery} />
          ) : (
            <p className="text-xs text-muted">
              Follow one external-credit order from the provider transaction to its local finalization.
            </p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
